import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators/field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

/**
 * What the user asked to have generated.
 *
 * Deliberately not a `MongoEntity`: this is the interface between client, server and worker, not a record.
 * The client fills it in the form, the mutation hands it to the task queue verbatim, and the worker consumes
 * it — the queue is what holds it in the meantime, and it is gone once the job is done. What survives a run
 * is the `GenerationLog`, which records the outcome without any of the data below.
 *
 * Nothing about *who* asked or *how it went* lives here either: the owner rides along on the queue job and
 * the status is the task's, read back from the queue as a `GenerationTask`.
 */
@Entity()
export class GenerateProcess extends AdornisEntity {
  static override _class = 'GenerateProcess';

  constructor(doc: PartialEntityData<GenerateProcess>) {
    super(doc);
  }

  /**
   * The organization this run belongs to. Decides which template fields form the
   * extraction catalog and which extraction prompt/model are used, and every
   * selected template must belong to it.
   */
  @Field(() => ID)
  organizationId: Maybe<string>;

  /**
   * Which templates should be filled. These are `Template` ids; the server
   * resolves them to the underlying `.docx` files after checking ownership.
   */
  @Field(() => [ID])
  templateIds: Maybe<string[]>;

  /**
   * The uploaded source documents the values are extracted from.
   */
  @Field(() => [ID])
  documentIds: Maybe<string[]>;

  /**
   * The user can give the extraction AI multiple extractions to perform.
   */
  @Field(() => String)
  additionalInstructions: Maybe<string>;

  /**
   * Set by the server on the way back: the id of the queued task. The client follows the run with it via
   * `subscribeGenerationTasks`, and it is the key the resulting `GenerationLog` is written under.
   */
  @Field(() => ID)
  taskId: Maybe<string>;
}
